/**
 * @summary - Component that Provides Refresh and Export(csv,xlxs) functionality
 * @author Purushothaman T
 * @since 2024-05-30
 */
import LoopRoundedIcon from "@mui/icons-material/LoopRounded";
import SaveAltRoundedIcon from "@mui/icons-material/SaveAltRounded";
import PrintIcon from '@mui/icons-material/Print';
import { IconButton, Stack } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Tooltip from "@mui/material/Tooltip";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import moment from "moment";
import { saveAs } from "file-saver";
import reportslogo from "../../assets/images/reportslogo.png";
import ExcelJS from 'exceljs';
import { useSelector } from 'react-redux';
// import { Provider } from 'react-redux';
import { Buffer } from 'buffer';
import { useAccess } from "../../AccessContext";
import utilities from "../../utility/utilities";

const RefreshAssetButtons = ({
  sheetname,
  filename,
  refData,
  handleRefresh,
  reportLevel,
  commonTableDetails,
  enablePdfButton=true,
  enableprintButton=true,
  headerValues

}) => {

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGA002");

  const gridApi = refData;
 
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState(true);

  const getLevel4Data = useSelector((state) => state.assetAnalysisReducer.sections);

  const handleGetDisplayedRowData = () => {
    if (gridApi) {
      // Get all displayed columns excluding the "actions" column
      const columns = gridApi
        .getAllDisplayedColumns()
        .filter((col) => col.getColDef().headerName !== "Actions");

      // Extract headers from the filtered columns
      const headers = columns
        .map((col) => col.getColDef().headerName)
        .filter((header) => header !== "Actions");

      // Get filtered rows data
      const displayedData = [];
      gridApi.forEachNodeAfterFilter((node) => {
        let rowData = {};
        columns.forEach((col) => {
          const colDef = col.getColDef();
          const headerName = colDef.headerName;
          rowData[headerName] = node.refData[colDef.field];
        });
        displayedData.push(rowData);
      });

      // If displayedData is empty, include headers only
      if (displayedData.length === 0) {
        let headerOnlyRow = {};
        headers.forEach((header) => {
          headerOnlyRow[header] = ""; // Fill with empty string or null as placeholder
        });
        return [headerOnlyRow];
      }

      return displayedData;
    } else {
      return [];
    }
  };



  const formatDate = (date) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const hiddenFields = ["typeCodeId", "locationCodeId", "assetId", "realTimestatusId"]; // Fields to remove

  const formatHeader = (header) => {
    // Add spaces between camelCase words and capitalize the first letter of each word
    return header.replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^[a-z]/, char => char.toUpperCase());
  };



    const exportPDF = async () => {
      const pdf = new jsPDF();
      
      pdf.setFontSize(7);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const leftMargin = 8;
      const adjustedPdfWidth = pdfWidth - leftMargin - margin;
      const topMargin = 40;
      const bottomMargin = 20; // Added bottom margin to prevent overwriting the footer
    
      if (!Array.isArray(refData) || refData.length === 0) {
        console.error("refData is not an array or is empty");
        return;
      }
    
   
    
      const flattenData = (data) => {
        return data.map(row => {
          const flatRow = {};
          Object.keys(row).forEach(key => {
            if (typeof row[key] === 'object' && !Array.isArray(row[key])) {
              Object.keys(row[key]).forEach(subKey => {
                flatRow[`${key} - ${subKey}`] = row[key][subKey];
              });
            } else {
              flatRow[key] = row[key];
            }
          });
          return flatRow;
        });
      };
    
      const flattenedData = flattenData(refData);
    
      if (flattenedData.length === 0) {
        console.error("No data available to export");
        return;
      }
      const desiredOrder = [
        "facilityName", // Hospital
        "level", // Level
        "option", // Option
        "femsAssetCount", // Total Asset FEMS
        "bemsAssetCount", // Total Asset BEMS
        "totalCount" // Total Asset
      ];
      
      // Get the headers from the first row in flattenedData and filter out hidden fields
      const headers = Object.keys(flattenedData[0])
        .filter(header => !hiddenFields.includes(header) && header !== "no"); // Remove "no"
      
      // Reorder the headers according to the desired order
      const sortedHeaders = [
        ...desiredOrder.filter(header => headers.includes(header)), 
        ...headers.filter(header => !desiredOrder.includes(header)) 
    ];
    
   
    
    // Map headers to their formatted display names
    const formattedHeaders = sortedHeaders.map(header => {
        switch (header) {
            case "femsAssetCount":
                return "Total Asset FEMS";
            case "bemsAssetCount":
                return "Total Asset BEMS";
            case "totalCount":
                return "Total Assets";
            case "facilityName":
                return "Facility Name";
            case "level":
                return "Level";
            case "option":
                return "Option";
            default:
                return header;
        }
    });
    
    
    
    // Ensure proper order for CSV export
    const modifiedHeaders = ["No", ...formattedHeaders];
    
  
    
    // Generate row data in correct order
    const rows = flattenedData.map((row, index) => {
        const filteredRow = sortedHeaders.map(header => { 
            let cell = row[header];
    
            // Format date fields
            if (typeof cell === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(cell)) {
                cell = moment(cell).format("DD/MM/YYYY");
            }
    
            return cell;
        });
    
        return [
            { content: index + 1, styles: { halign: "center", fontSize: 7 } }, // Row number
            ...filteredRow.map(cell => ({
                content: cell,
                styles: {
                    halign: typeof cell === "number" || !isNaN(cell) ? "right" : "center", // Align numbers to right
                    fontSize: 7,
                },
            })),
        ];
    });
    
   
    
    // Calculate the column widths
    const columnCount = modifiedHeaders.length;
    const columnWidth = adjustedPdfWidth / columnCount;

    // Generate the primary and secondary headers
    const primaryHeaders = [];
    const secondaryHeaders = [];
    let hasSecondaryHeaders = false;

    modifiedHeaders.forEach(header => {
      const parts = header.split(" - ");
      if (parts.length > 1) {
        if (!primaryHeaders.includes(parts[0])) {
          primaryHeaders.push(parts[0]);
          secondaryHeaders.push([]);
        }
        const parentIndex = primaryHeaders.indexOf(parts[0]);
        secondaryHeaders[parentIndex].push(parts[1]);
        hasSecondaryHeaders = true;
      } else {
        primaryHeaders.push(header);
        secondaryHeaders.push([]);
      }
    });

    const tableHeaders = hasSecondaryHeaders ? [
      primaryHeaders.map(header => {
        if (header === "Completed") {
          return { content: header, colSpan: 2, styles: { halign: 'center', fontSize: 7 } };
        }
        return { content: header, rowSpan: 2, styles: { halign: 'center', fontSize: 7} };
      }),
    
      secondaryHeaders.flat().map(subHeader => subHeader ? { content: subHeader } : { content: '' })
    ] : [
      primaryHeaders.map(header => ({ content: header, styles: { halign: 'center', fontSize: 7 } }))
    ];
    // Draw the header on the first page
    const drawHeader = (pdf, pageNumber) => {
      if (!pdf || !pdf.setFont || !pdf.setFontSize || !pdf.text || !pdf.setFont || !pdf.addImage || !pdf.setLineWidth || !pdf.setDrawColor || !pdfWidth || !filename || !reportslogo || !leftMargin || !margin) {
        console.error('Missing required parameter(s) in drawHeader function');
        return;
      }

      try {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        //  pdf.text(filename, 105, 20, { align: "center" });
        pdf.text(filename, pdfWidth / 2, 20, { align: "center" });
        pdf.setFont("helvetica", "normal");
        pdf.addImage(reportslogo, "PNG", pdfWidth - 47, 10, 30, 18);
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0);
        const headerBottomY = 33;
        pdf.line(leftMargin, headerBottomY, pdfWidth - margin, headerBottomY);
      } catch (error) {
        console.error('Error occurred in drawHeader function:', error);
      }

    };


    // Specific layout for level 3
    if (reportLevel === 3) {
      drawHeader(pdf, 1);
    
      // Declare and initialize currentY
      let currentY = topMargin;
    
      // const tableElement = document.querySelector(".table-container");
    
      // // Convert the HTML table to an image and add it to the PDF
      // if (tableElement) {
      //   const canvas = await html2canvas(tableElement);
      //   const imgData = canvas.toDataURL("image/png");
      //   const imgHeight = (canvas.height * adjustedPdfWidth) / canvas.width;
    
      //   pdf.addImage(imgData, "PNG", leftMargin, currentY, adjustedPdfWidth, imgHeight);
    
      //   // Update currentY to position other content below the table
      //   currentY += imgHeight + 10;
      // } else {
      //   console.error("Table element '.table-container' not found");
      // }
    // Create a single table body with a title row at the top
// Create a single table body with a title row at the top
const tableData = [
  // First row with "Hospital" details
  [
    {
      content: `Hospital:`,
      styles: {
        halign: "left",
        fillColor: [245, 245, 245], // Light gray background
        textColor: "black",
        fontStyle: "bold",
        fontSize: 7,
        cellPadding: 2,
        lineColor: [200, 200, 200], // Light gray border
        lineWidth: 0.5, // Thin border
      },
    }, {
      content: `${headerValues.hospital}`, // Hospital name in a separate box
      styles: {
        halign: "left",
        fillColor: [255, 255, 255], // White background
        textColor: "black",
        fontStyle: "normal",
        fontSize: 7,
        cellPadding: 2,
        lineColor: [200, 200, 200], // Light gray border
        lineWidth: 0.5, // Thin border
      },
    },
    {
      content: `Group By: `,
      styles: {
        halign: "left",
        fillColor: [245, 245, 245], // White background
        textColor: "black",
        fontStyle: "bold",
        fontSize: 7,
        cellPadding: 2,
        lineColor: [200, 200, 200], // Light gray border
        lineWidth: 0.5, // Thin border
      },
    },
    {
      content: `${ headerValues.groupby}`, // Hospital name in a separate box
      styles: {
        halign: "left",
        fillColor: [255, 255, 255], // White background
        textColor: "black",
        fontStyle: "normal",
        fontSize: 7,
        cellPadding: 2,
        lineColor: [200, 200, 200], // Light gray border
        lineWidth: 0.5, // Thin border
      },
    },
    {
      content: `Asset Status:`,
      styles: {
        halign: "left",
        fillColor: [245, 245, 245], // Light gray background
        textColor: "black",
        fontStyle: "bold",
        fontSize: 7,
        cellPadding: 2,
        lineColor: [200, 200, 200], // Light gray border
        lineWidth: 0.5, // Thin border
      },
    },
    {
      content: `${ headerValues.assetstatus}`, // Hospital name in a separate box
      styles: {
        halign: "left",
        fillColor: [255, 255, 255], // White background
        textColor: "black",
        fontStyle: "normal",
        fontSize: 7,
        cellPadding: 2,
        lineColor: [200, 200, 200], // Light gray border
        lineWidth: 0.5, // Thin border
      },
    },
  ],
];

// Render the table
pdf.autoTable({
  body: tableData,
  // startY: currentY,
  theme: "striped",
  styles: {
    fontSize: 7,
    lineColor: [192, 192, 192],
    lineWidth: 0.2,
    cellPadding: 2,
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    overflow: "linebreak",
  },
  headStyles: { fillColor: "#009999", textColor: "white", fontStyle: "bold" },
  margin: { left: leftMargin, top: topMargin, bottom: bottomMargin },
  didDrawPage: function (data) {
    drawHeader(pdf, data.pageNumber);
  },
  showHead: "everyPage", // Only headers repeat (not title rows)
});

// Update currentY after the table
currentY = pdf.autoTable.previous.finalY + 10;


      // Process the remaining sections
      const sections = getLevel4Data; // Assuming this provides the array of section data
    
      sections.forEach((section, index) => {
        // Check if a new page is needed before rendering a section
        if (index > 0 && currentY + 10 > pdfHeight - bottomMargin) {
          pdf.addPage();
          drawHeader(pdf, pdf.internal.getCurrentPageInfo().pageNumber);
          currentY = topMargin;
        }
    
        // Prepare table headers and rows
        const headers = section.data[0] ? Object.keys(section.data[0]) : [];
        const rows = section.data.map(row => Object.values(row));
   
        // Create a single table body with a title row at the top
        const tableData = [
          [
            {
              content: section.title, // Section title
              colSpan: headers.length, // Span the title across all columns
              styles: {
                halign: "center", // Center-align the title
                fillColor: "#009999",
               
                textColor: "white",
                fontStyle: "bold",
                fontSize: 7,
                cellPadding: 3,
              },
            },
          ],
          // ...rows, // Append the data rows below the title
        ];
    
        // Render the table
        pdf.autoTable({
          body: tableData,
          startY: currentY,
          theme: "striped",
          styles: {
            fontSize:7,
            lineColor: [192, 192, 192],
            lineWidth: 0.5,
            cellPadding: 2,
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            overflow: "linebreak",
          },
          headStyles: { fillColor: "#009999", textColor: "white", fontStyle: "bold" },
          margin: { left: leftMargin, top: topMargin, bottom: bottomMargin },
          columnStyles: headers.reduce((styles, _, colIndex) => {
            styles[colIndex] = { cellWidth: "auto", halign: "left" }; // Adjust all columns to auto width
            return styles;
          }, {}),
          didDrawPage: function (data) {
            drawHeader(pdf, data.pageNumber);
          },
          showHead: "everyPage", // Only headers repeat (not title rows)
        });
    
        // Update currentY after the table
        currentY = pdf.autoTable.previous.finalY + 10;
      });
    
      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      const dateStr = new Date().toLocaleString();
    
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        const pageStr = `Date & Time: ${dateStr}`;
        const pageNumStr = `Page ${i} of ${totalPages}`;
        pdf.text(pageStr, margin, pdfHeight - 10);
        pdf.text(pageNumStr, pdfWidth - pdf.getTextWidth(pageNumStr) - margin, pdfHeight - 10);
        const footerY = pdf.internal.pageSize.height - bottomMargin;
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0);
        pdf.line(leftMargin, footerY, pdfWidth - margin, footerY);
      }
    }
    else {
      // Default layout for other levels
      drawHeader(pdf, 1);

      // // Convert table element to image
      // const tableElement = document.querySelector(".table-container");
      // const canvas = await html2canvas(tableElement);
      // const imgData = canvas.toDataURL("image/png");
      // const imgHeight = (canvas.height * adjustedPdfWidth) / canvas.width;

      // // Add table image to PDF
      // pdf.addImage(imgData, "PNG", leftMargin, topMargin, adjustedPdfWidth, imgHeight);
      // Create a single table body with a title row at the top
      const tableData = [
        // First row with "Hospital" details
        [
          {
            content: `Hospital:`,
            styles: {
              halign: "left",
              fillColor: [245, 245, 245], // Light gray background
              textColor: "black",
              fontStyle: "bold",
              fontSize: 7,
              cellPadding: 2,
              lineColor: [200, 200, 200], // Light gray border
              lineWidth: 0.5, // Thin border
            },
          }, {
            content: `${headerValues.hospital}`, // Hospital name in a separate box
            styles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background
              textColor: "black",
              fontStyle: "normal",
              fontSize: 7,
              cellPadding: 2,
              lineColor: [200, 200, 200], // Light gray border
              lineWidth: 0.5, // Thin border
            },
          },
          {
            content: `Group By: `,
            styles: {
              halign: "left",
              fillColor: [245, 245, 245], // White background
              textColor: "black",
              fontStyle: "bold",
              fontSize: 7,
              cellPadding: 2,
              lineColor: [200, 200, 200], // Light gray border
              lineWidth: 0.5, // Thin border
            },
          },
          {
            content: `${ headerValues.groupby}`, // Hospital name in a separate box
            styles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background
              textColor: "black",
              fontStyle: "normal",
              fontSize: 7,
              cellPadding: 2,
              lineColor: [200, 200, 200], // Light gray border
              lineWidth: 0.5, // Thin border
            },
          },
          {
            content: `Asset Status:`,
            styles: {
              halign: "left",
              fillColor: [245, 245, 245], // Light gray background
              textColor: "black",
              fontStyle: "bold",
              fontSize: 7,
              cellPadding: 2,
              lineColor: [200, 200, 200], // Light gray border
              lineWidth: 0.5, // Thin border
            },
          },
          {
            content: `${ headerValues.assetstatus}`, // Hospital name in a separate box
            styles: {
              halign: "left",
              fillColor: [255, 255, 255], // White background
              textColor: "black",
              fontStyle: "normal",
              fontSize: 7,
              cellPadding: 2,
              lineColor: [200, 200, 200], // Light gray border
              lineWidth: 0.5, // Thin border
            },
          },
        ],
      ];
      

// Render the table
pdf.autoTable({
  body: tableData,

  theme: "striped",
  styles: {
    fontSize: 7,
    lineColor: [192, 192, 192],
    lineWidth: 0.2,
    cellPadding: 2,
    fillColor: [255, 255, 255],
    textColor: [0, 0, 0],
    overflow: "linebreak",
  },
  headStyles: { fillColor: "#009999", textColor: "white", fontStyle: "bold" },
  margin: { left: leftMargin, top: topMargin, bottom: bottomMargin },
  columnStyles: headers.reduce((styles, _, colIndex) => {
    styles[colIndex] = { cellWidth: "auto", halign: "left" }; // Adjust all columns to auto width
    return styles;
  }, {}),
  didDrawPage: function (data) {
    drawHeader(pdf, data.pageNumber);
  },
  showHead: "everyPage", // Only headers repeat (not title rows)
});

// Update currentY after the table
// currentY = pdf.autoTable.previous.finalY + 10;

    
      // Add the table content
      pdf.autoTable({
        head: tableHeaders,
        body: rows,
        // startY: topMargin + imgHeight + 1, // Reduced space between the table image and the table content
        theme: "striped",
        styles: {
          fontSize: 7,
          lineColor: [192, 192, 192],
          lineWidth: 0.5,
          cellPadding: 2,
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          overflow: 'linebreak', // Ensure text wraps correctly within cell
        },
        headStyles: { fillColor: '#D3D3D3', textColor: "#000000" },
        margin: { left: leftMargin, top: topMargin, bottom: bottomMargin },

        columnStyles: Array(columnCount).fill({ cellWidth: columnWidth }),
        didDrawPage: function (refData) {
          // Ensure header is drawn on every page
          drawHeader(pdf, refData.pageNumber);
        },
      });

      const totalPages = pdf.internal.getNumberOfPages();
      const dateStr = new Date().toLocaleString();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        const pageStr = `Date & Time: ${dateStr}`;
        const pageNumStr = `Page ${i} of ${totalPages}`;
        pdf.text(pageStr, margin, pdfHeight - 10);
        pdf.text(pageNumStr, pdfWidth - pdf.getTextWidth(pageNumStr) - margin, pdfHeight - 10);
        const footerY = pdf.internal.pageSize.height - bottomMargin;
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0);
        pdf.line(leftMargin, footerY, pdfWidth - margin, footerY); // Adjusted margin for footer line
      }
    }

    pdf.save("Asset Analysis.pdf");
  };



  // function for reports Print
  const onPrint = async () => {
    const originalPagination = pagination;
    setPagination(false);

    await new Promise((resolve) => setTimeout(resolve, 100)); // Wait for grid to re-render without pagination

    const pdf = new jsPDF();
    pdf.setFontSize(10);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 17;
    const leftMargin = 17;
    const adjustedPdfWidth = pdfWidth - leftMargin - margin;
    const topMargin = 35;

    // Ensure refData is an array
    if (!Array.isArray(refData)) {
      setPagination(originalPagination);
      return;
    }

    const flattenData = (refData) => {
      return refData.map(row => {
        const flatRow = {};
        Object.keys(row).forEach(key => {
          if (typeof row[key] === 'object' && !Array.isArray(row[key])) {
            Object.keys(row[key]).forEach(subKey => {
              flatRow[`${key} - ${subKey}`] = row[key][subKey];
            });
          } else {
            flatRow[key] = row[key];
          }
        });
        return flatRow;
      });
    };

    // Flatten the refData
    const flattenedData = flattenData(refData);

    if (flattenedData.length === 0) {

      setPagination(originalPagination);
      return;
    }

    // Modify headers and rows: Move "no" column to the first position and capitalize "no" values

 // Define the exact desired order of headers
 const formatHeader = (header) => {
  const headerMap = {
    facilityName: "Hospital",
    level: "Level",
    option: "Option",
    femsAssetCount: "Total Asset FEMS",
    bemsAssetCount: "Total Asset BEMS",
    totalCount: "Total Assets"
  };
  return headerMap[header] || header; // Return mapped name or original if not found
};

const desiredOrder = [
  "facilityName",  // Hospital
  "level",         // Level
  "option",        // Option
  "femsAssetCount", // Total Asset FEMS
  "bemsAssetCount", // Total Asset BEMS
  "totalCount"     // Total Assets
];

// Get all the keys from the first object in flattenedData, excluding "no"
const headers = Object.keys(flattenedData[0])
  .filter(header => header !== "no" && !hiddenFields.includes(header)); // Remove hidden fields and "no"

// Reorder headers based on the desired order, ensuring only available headers are included
const sortedHeaders = [
  ...desiredOrder.filter(header => headers.includes(header)), // Include only available headers from desiredOrder
  ...headers.filter(header => !desiredOrder.includes(header)) // Append remaining headers not in desiredOrder
];



// Format headers properly with correct names and exclude "no"
const modifiedHeaders = ["No", ...sortedHeaders.map(header => formatHeader(header))];


// Map through the flattenedData to generate the rows
const rows = flattenedData.map((row, index) => {
  const filteredRow = sortedHeaders.map(header => row[header] || ""); // Ensure correct ordering and prevent undefined values
  return [index + 1, ...filteredRow]; // Add the row number as "No"
});





    // Generate the primary and secondary headers
    const primaryHeaders = [];
    const secondaryHeaders = [];
    let hasSecondaryHeaders = false;

    modifiedHeaders.forEach(header => {
      const parts = header.split(" - ");
      if (parts.length > 1) {
        if (!primaryHeaders.includes(parts[0])) {
          primaryHeaders.push(parts[0]);
          secondaryHeaders.push([]);
        }
        const parentIndex = primaryHeaders.indexOf(parts[0]);
        secondaryHeaders[parentIndex].push(parts[1]);
        hasSecondaryHeaders = true;
      } else {
        primaryHeaders.push(header);
        secondaryHeaders.push([]);
      }
    });

    const tableHeaders = hasSecondaryHeaders ? [
      primaryHeaders.map(header => {
        if (header === "Completed") {
          return { content: header, colSpan: 2, styles: { halign: 'center' } };
        }
        return { content: header, rowSpan: 2, styles: { halign: 'center' } };
      }),
      secondaryHeaders.flat().map(subHeader => subHeader ? { content: subHeader } : { content: '' })
    ] : [
      primaryHeaders.map(header => ({ content: header, styles: { halign: 'center' } }))
    ];

    const drawHeader = (pdf, pageNumber) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.text(filename, 105, 20, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.addImage(reportslogo, "PNG", pdfWidth - 47, 10, 30, 18);
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(0);
      const headerBottomY = 33;
      pdf.line(leftMargin, headerBottomY, pdfWidth - margin, headerBottomY);
    };

    // Specific layout for level 3
    if (reportLevel === 3) {
      drawHeader(pdf, 1);

      // Convert first table element to image
      const tableElement1 = document.querySelector(".table-container");
      const canvas1 = await html2canvas(tableElement1);
      const imgData1 = canvas1.toDataURL("image/png");
      const imgHeight1 = (canvas1.height * adjustedPdfWidth) / canvas1.width;

      // Add first table image to PDF
      pdf.addImage(imgData1, "PNG", leftMargin, topMargin, adjustedPdfWidth, imgHeight1);

      // Convert second table element to image
      const tableElement2 = document.querySelector(".finalreport-table-container");
      const canvas2 = await html2canvas(tableElement2);
      const imgData2 = canvas2.toDataURL("image/png");
      const imgHeight2 = (canvas2.height * adjustedPdfWidth) / canvas2.width;

      // Check if the second table fits on the current page, otherwise add a new page
      let currentY = topMargin + imgHeight1 + 10; // Adjust 10 for spacing between tables
      if (currentY + imgHeight2 > pdfHeight - margin) {
        pdf.addPage();
        currentY = topMargin;
      }

      // Add second table image to PDF
      pdf.addImage(imgData2, "PNG", leftMargin, currentY, adjustedPdfWidth, imgHeight2);

      const totalPages = pdf.internal.getNumberOfPages();
      const dateStr = new Date().toLocaleString();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        const pageStr = `Date & Time: ${dateStr}`;
        const pageNumStr = `Page ${i} of ${totalPages}`;
        pdf.text(pageStr, margin, pdfHeight - 10);
        pdf.text(pageNumStr, pdfWidth - pdf.getTextWidth(pageNumStr) - margin, pdfHeight - 10);
        const footerY = pdf.internal.pageSize.height - 14;
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0);
        pdf.line(leftMargin, footerY, pdfWidth - margin, footerY);
      }

      pdf.autoPrint();
      window.open(pdf.output("bloburl"), "_blank");

      setPagination(originalPagination);
    } else {
      // Default layout for other levels
      drawHeader(pdf, 1);

      const tableElement = document.querySelector(".table-container");
      const canvas = await html2canvas(tableElement);
      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * adjustedPdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", leftMargin, topMargin, adjustedPdfWidth, imgHeight);

      pdf.autoTable({
        head: tableHeaders,
        body: rows,
        startY: topMargin + imgHeight + 1,
        theme: "striped",
        styles: {
          fontSize: 8,
          lineColor: [192, 192, 192],
          lineWidth: 0.5,
          cellPadding: 2,
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
        tableWidth: adjustedPdfWidth,
        headStyles: { fillColor: '#152B75', textColor: [255, 255, 255] },
        margin: { left: leftMargin, top: 35 },
        columnStyles: {
          0: { cellWidth: 'auto' },
        },
        didDrawPage: function (refData) {
          drawHeader(pdf, refData.pageNumber);
        },
      });

      const totalPages = pdf.internal.getNumberOfPages();
      const dateStr = new Date().toLocaleString();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        const pageStr = `Date & Time: ${dateStr}`;
        const pageNumStr = `Page ${i} of ${totalPages}`;
        pdf.text(pageStr, margin, pdfHeight - 10);
        pdf.text(pageNumStr, pdfWidth - pdf.getTextWidth(pageNumStr) - margin, pdfHeight - 10);
        const footerY = pdf.internal.pageSize.height - 14;
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(0);
        pdf.line(leftMargin, footerY, pdfWidth - margin, footerY);
      }

      pdf.autoPrint();
      window.open(pdf.output("bloburl"), "_blank");

      setPagination(originalPagination);
    }
  };



  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleGridRefresh = () => {
    gridApi.setFilterModel(null);
    gridApi.paginationProxy.setPageSize(10, "pageSizeSelector");
    const columns = gridApi.getColumns();
    const hiddenColumns = columns.filter((column) => !column.visible);
    hiddenColumns.forEach((column) => {
      gridApi.setColumnVisible(column, true);
    });
    gridApi.refreshHeader();
    handleRefresh();
  };

  // Function for the excel
  const exportToExcel = async () => {
    if (reportLevel === 3) {
      // Create a new workbook and add a worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Report');
    
      // Load and add the right logo
      const rightLogoImage = await fetch(reportslogo).then(response => response.arrayBuffer());
      const rightLogoId = workbook.addImage({
        buffer: rightLogoImage,
        extension: 'png',
      });
      worksheet.addImage(rightLogoId, {
        tl: { col: 9, row: 2 }, // Position the image
        ext: { width: 80, height: 80 },
        editAs: 'oneCell',
      });
    
      // Set up the column widths
      worksheet.columns = Array(10).fill({ width: 15 });
    
      // Add a heading
      worksheet.mergeCells('A2:J6');
      const titleCell = worksheet.getCell('A4');
      titleCell.value = 'ASSET ANALYSIS, LISTING AND SUMMARY HISTORY';
      titleCell.font = { size: 15, bold: true };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    
      // Define and add sections with formatting
      const sections = getLevel4Data; // Assuming this contains the section data

      // Add each section to the worksheet
      let titleRow = 8; // Start the first section after the heading
      
      const addSection = (section) => {
        // Add a blank row for spacing
        worksheet.addRow([]);
      
        // Add section title and merge cells
        worksheet.mergeCells(`A${titleRow}:J${titleRow}`);
      
        // Define section title cell and apply styles
        const sectionTitleCell = worksheet.getCell(`A${titleRow}`);
        sectionTitleCell.value = section.title || 'Section Title'; // Replace 'Section Title' with actual section title
        sectionTitleCell.font = { size: 14, bold: true };
        sectionTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        sectionTitleCell.fill = {
          type: 'pattern',
          pattern: 'solid',
           fgColor: { argb: 'FFD3D3D3' }, // Grey background
        };
        sectionTitleCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      
        // Increment titleRow for data rows
        titleRow += 6;
      
        // Define a helper function to apply cell styles
        const applyCellStyles = (cell) => {
          cell.font = { size: 12 };
          cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' }, // White background
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        };
      
        // Add data rows for the section
        section.data.forEach((row) => {
          const rowIndex = worksheet.addRow(row).number;
  
          // Apply styles to each cell
          row.forEach((value, columnIndex) => {
            const cell = worksheet.getCell(rowIndex, columnIndex + 1); // columnIndex + 1 because columns start at 1
            cell.value = value;
            applyCellStyles(cell);
          });
        });
     
        // Update titleRow for the next section
        titleRow += 3; // Move to the next section, 3 rows below
      };
      
      // Loop through each section and add it to the worksheet
      sections.forEach(addSection); // This ensures that each section is added correctly
      
      // Try-catch block for generating and saving the Excel file
      try {
        const buffer = await workbook.xlsx.writeBuffer(); // Generate the buffer
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'ASSET ANALYSIS REPORT.xlsx');
      } catch (error) {
        console.error('Error writing Excel file:', error);
      }
    }
    
    
    else {

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Report');

      // Load and add the right logo
      const rightLogoImage = await fetch(reportslogo).then(response => response.arrayBuffer());
      const rightLogoId = workbook.addImage({
        buffer: rightLogoImage,
        extension: 'png',
      });

      worksheet.addImage(rightLogoId, {
        tl: { col: 5.5, row: 1.5 },
        ext: { width: 80, height: 80 },
        editAs: 'oneCell'
      });

      // Merge cells to create a gap for the title
      worksheet.mergeCells('A2:F6');
      const titleCell = worksheet.getCell('A4');
      titleCell.value = filename;
      titleCell.font = { size: 15, bold: true };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

      // Add some empty rows for spacing
      worksheet.addRow([]);
      worksheet.addRow([]);

      const formatHeader = (header) => {
        return header.replace(/([A-Z])/g, ' $1') // Add space before each uppercase letter
          .replace(/^./, str => str.toUpperCase()) // Capitalize the first letter
          .trim(); // Remove leading and trailing spaces
      };

      // List of hidden fields to exclude
      const hiddenFields = ["typeCodeId", "locationCodeId", "assetId", "realTimestatusId"];



      const originalKeys = Object.keys( headerValues)
        .filter(key => key !== 'title' && !hiddenFields.includes(key));

        const filteredKeys = originalKeys.filter(key => !hiddenFields.includes(key)); // Exclude hidden fields

        const tableHeaders = filteredKeys.map(key => formatHeader(key));
        // const tableData = [filteredKeys.map(key => commonTableDetails[key] || '')];
        
  
 
        

      const tableData = [originalKeys.map(key => headerValues[key] || '')];

       // Add the table headers
      const headerRow = worksheet.addRow(tableHeaders);
      headerRow.font = { bold: true };

      // Add the table data
      tableData.forEach(row => {
        worksheet.addRow(row);
      });

      // Add some empty rows for spacing
      worksheet.addRow([]);

   


const allKeys = [...new Set(refData.flatMap(obj => Object.keys(obj)))];


const agGridHeaders = [ ...allKeys
    .filter(key => key !== 'no' && !hiddenFields.includes(key)) // Filter out hidden fields
    .map(key => formatHeader(key))
];

// Step 3: Add header row
const agGridHeaderRow = worksheet.addRow(agGridHeaders);
agGridHeaderRow.font = { bold: true };



// Step 4: Loop through data and ensure each row has all required fields
refData.forEach(rowData => {
    const reorderedRow = [
        // rowData['no'],
        ...allKeys
            .filter(key => key !== 'no' && !hiddenFields.includes(key)) // Filter hidden fields
            .map(key => rowData[key] !== undefined ? rowData[key] : "") // Fill missing values with ""
    ];

    worksheet.addRow(reorderedRow);
   
});


      const maxColumnWidths = [];
      worksheet.columns.forEach((column, index) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const cellValue = cell.value ? cell.value.toString() : '';
          maxLength = Math.max(maxLength, cellValue.length);
        });
        column.width = maxLength + 2; // Add some padding to the width
        maxColumnWidths[index] = maxLength + 2;
      });

      // Apply some basic styling
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
      });

      // Merge cells in the middle table to match the width of the data table
      const mergeStartColumn = agGridHeaderRow.cellCount + 1;
      const mergeEndColumn = mergeStartColumn + maxColumnWidths.length - 1;
      for (let i = 9; i <= 10; i++) {
        worksheet.mergeCells(i, mergeStartColumn, i, mergeEndColumn);
      }

      // Save the workbook
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), 'ASSET ANALYSIS REPORT.xlsx');
    }
  };





  return (
    <>
      <Stack direction={"row"} alignItems={"flex-end"}>
        {sheetname !== "asset" && (
          <Tooltip
            title="Refresh"
            placement="bottom"
            arrow
            disableFocusListener
            disableInteractive
          >
            <IconButton onClick={handleGridRefresh}>
              <LoopRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        {access?.canPrint && <Tooltip
          title="Export"
          placement="bottom"
          arrow
          disableFocusListener
          disableInteractive
        >
          <IconButton
            variant="contained"
            ref={anchorRef}
            onClick={handleToggle}
          >
            <SaveAltRoundedIcon />
          </IconButton>
        </Tooltip>}
      </Stack>
      {(access?.canPrint && enableprintButton) && <Stack direction={"row"} alignItems={"flex-end"}>
        <Tooltip
          title="Print"
          placement="bottom"
          arrow
          disableFocusListener
          disableInteractive
        >
          <IconButton onClick={onPrint}>
            <PrintIcon />
          </IconButton>
        </Tooltip>

      </Stack>}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        sx={{ zIndex: 5, width: 100 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >


                  {sheetname === "asset" && (
                    <>
                     {enablePdfButton && <MenuItem onClick={exportPDF}>pdf</MenuItem>}
                     {/* {enablePdfButton &&   */}
                     <MenuItem onClick={exportToExcel}>Excel</MenuItem>
                     {/* } */}
                    </>
                  )}

                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default RefreshAssetButtons;
